import type { TFunction } from "i18next";

import {
  LatestIngestion,
  LatestIngestionView,
  StaticTableIngestionType,
} from "shared/api/ingestion/api";
import { LONG_DATE_FORMAT, MONTH_DAY, TIME_ONLY } from "shared/constants";
import { formatDate } from "shared/locale/format";
import { getUserLocale } from "shared/locale/getUserLocale";
import { EventTypeEnum } from "shared/types";

import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { PagesConfigModel } from "pages/SuperAdmin/PagesConfig/types";

import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";

import {
  INGESTION_TODAY,
  INGESTION_YESTERDAY,
  IngestionDateTime,
  IngestionEntry,
} from "./constants";

export const formatToHumanFriendlyDate = (
  utcTimestamp: number,
  utcNow: number,
  locale: string = getUserLocale()
): IngestionDateTime => {
  const date = new Date(utcTimestamp);
  const now = new Date(utcNow);

  const isSameDayLocal = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  let dateLabel: string;
  if (isSameDayLocal(date, now)) {
    dateLabel = INGESTION_TODAY;
  } else if (isSameDayLocal(date, yesterday)) {
    dateLabel = INGESTION_YESTERDAY;
  } else {
    dateLabel = formatDate(
      utcTimestamp,
      LONG_DATE_FORMAT,
      false,
      false,
      undefined,
      locale
    );
  }

  const timeLabel = formatDate(utcTimestamp, TIME_ONLY);

  return {
    dateTimestamp: utcTimestamp,
    dateLabel,
    timeLabel,
  };
};

const STATIC_TABLE_LABEL_KEYS: Record<StaticTableIngestionType, string> = {
  claim: "common:event_type.claim_other",
  signalEvent: "common:event_type.signalevent_other",
  vehicle: "common:event_type.vehicle_other",
};

export const toIngestionEntry = (
  entry: LatestIngestion,
  t: TFunction,
  utcNow: number = Date.now()
): IngestionEntry => {
  const { ingestedAt, windowWeeks } = entry;

  return {
    entityType:
      entry.displayName === null
        ? t(STATIC_TABLE_LABEL_KEYS[entry.type])
        : entry.displayName,
    ...(ingestedAt
      ? {
          ingestedAt: formatToHumanFriendlyDate(
            new Date(ingestedAt).getTime(),
            utcNow
          ),
        }
      : {
          ingestedAt: null,
          noEntryReason: t("common:ingestion.no_data_received_weeks", {
            count: windowWeeks,
          }),
        }),
  };
};

const isIngestionView = (
  view: LandingPageViewConfig
): view is LandingPageViewConfig & {
  eventType: LatestIngestionView["eventType"];
} =>
  view.eventType === EventTypeEnum.CLAIM ||
  view.eventType === EventTypeEnum.SIGNAL_EVENT;

export const getLatestIngestionViews = (
  pages: PagesConfigModel,
  isSuperAdmin: boolean
): LatestIngestionView[] =>
  (pages.landingPage?.views ?? [])
    .filter((view) => !view.beta || isSuperAdmin)
    .filter(isIngestionView)
    .map((view) => ({
      title: view.title,
      eventType: view.eventType,
      filter: getFiltersQuery(view.staticEventsFilter),
      vehiclesFilter: getFiltersQuery(view.staticVehiclesFilter),
      windowWeeks: view.lastIngestionWeeksCheck,
    }));

// Map the stable internal dateLabel sentinels to translated display text,
// leaving already-locale-formatted dates untouched. Comparisons stay on the
// locale-independent sentinels rather than the presented strings.
export const getIngestionDateLabel = (
  dateLabel: IngestionDateTime["dateLabel"],
  t: TFunction
): string => {
  if (dateLabel === INGESTION_TODAY) return t("ingestion.today");

  if (dateLabel === INGESTION_YESTERDAY) return t("ingestion.yesterday");

  return dateLabel;
};

// Format date for button display (abbreviated format), e.g. "Feb 16".
// Formats from the raw timestamp rather than re-parsing the localized
// dateLabel, which would fail for non-English locales.
export const getButtonDateLabel = (
  ingestedAt: IngestionDateTime | null | undefined,
  t: TFunction
): string => {
  if (!ingestedAt) return "";

  const { dateLabel, dateTimestamp } = ingestedAt;
  if (dateLabel === INGESTION_TODAY || dateLabel === INGESTION_YESTERDAY) {
    return getIngestionDateLabel(dateLabel, t);
  }

  return formatDate(dateTimestamp, MONTH_DAY);
};
